import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import useAPI from 'src/hooks/useAPI';
import { useOrderPageContext } from './context/context';
import getPlanBenefitsListAndValue from 'src/components/SubscriptionBenefitsBreakdown/utils';
// import { push, addSkuPath, cartPath } from 'src/utils/paths';
import { push, cartPath, loginPath } from 'src/utils/paths';
import { rebookOrder } from './actions';
import { logger } from 'src/utils/logger';
import { contextActions } from './context/ducks';
import { DEEP_LINK_QUERY_PARAMS } from './constants';
import { fromJS } from 'immutable';
import { immutableToJS } from 'src/utils/helpers';
import { ORDER_STATUS } from 'src/constants/order';
import { pureUserSelector } from 'src/selectors/user';

/**
 * We are going to immediately remove the "add_plan_to_order" query param from the URL
 * so that the state of the toast confirmations (e.g. "plan added", "plan already added")
 * can be synced correctly.
 */
const updateQueryParams = (history) => {
  const currentSearch = new URLSearchParams(window.location.search);
  currentSearch.delete(DEEP_LINK_QUERY_PARAMS.ADD_PLAN_TO_ORDER);

  history.replace({
    pathname: window.location.pathname,
    search: currentSearch.toString(),
  });
};

const sanitizeURLSearchParams = (location) => {
  const searchParams = new URLSearchParams(location.search);

  searchParams.delete('auth_token');

  const queryString = searchParams.toString();

  return {
    queryParams: Object.fromEntries(searchParams.entries()) || {},
    queryString: queryString ? `?${queryString}` : '',
  };
};

/**
 *
 * @returns {Object} {
 *   toggleUpsellModal: Function to toggle the modal's visibility,
 *   isUpsellModalOpen: Boolean for the modal's visibility,
 *   queryParams: Object containing all sanitized query parameters,
 *   queryString: String representation of the sanitized query parameters for appending to URLs.
 * }
 */
export const useDeepLinkModalControl = ({ canShowUpsell }) => {
  const location = useLocation();
  const history = useHistory();
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const toggleUpsellModal = () => setIsUpsellModalOpen(!isUpsellModalOpen);

  const { queryString, queryParams } = sanitizeURLSearchParams(location);

  /**
   * The OrderPage sagas.js forces a route replacement to include a status (e.g. `/orders/123` will be replaced
   * with `/orders/123/completed`) on page load. The replacement causes OrderPage.js to unmount and remount,
   * so we need to use useRef here to maintain the state of the upsell modal on page load.
   */
  useEffect(() => {
    if (canShowUpsell && queryParams[DEEP_LINK_QUERY_PARAMS.ADD_PLAN_TO_ORDER]) {
      updateQueryParams(history);
      setIsUpsellModalOpen(true);
    }
  }, [canShowUpsell, queryParams]);

  return {
    toggleUpsellModal,
    isUpsellModalOpen,
    queryParams,
    queryString,
  };
};

export const useOrderSubscriptionFetch = () => {
  const [, contextDispatch] = useOrderPageContext();
  const api = useAPI();

  const fetchPlanDetailsAndSetBenefits = async (planId) => {
    try {
      const response = await api.plans.subscriptions.show({ id: planId });
      if (response.err) throw new Error(response.err);
      const { plan: planWithClientDetails } = response.data;
      const planBenefits = getPlanBenefitsListAndValue({ plan: fromJS(planWithClientDetails) });
      contextDispatch(contextActions.setPlanDetails({ planDetails: planWithClientDetails }));
      contextDispatch(contextActions.setUpsellPlanBenefits({ upsellPlanBenefits: planBenefits }));
    } catch (e) {
      logger('OrderPage - useOrderSubscriptionFetch - error', e);
    }
  };

  return {
    fetchPlanDetailsAndSetBenefits,
  };
};

/**
 *
 * @param {Object} params
 * @param {Order} params.order
 */
export const useRebookOrder = () => {
  const dispatch = useDispatch();
  const user = useSelector(pureUserSelector);

  const location = useLocation();
  const history = useHistory();

  const { queryParams } = sanitizeURLSearchParams(location);

  const canRebookOrder = (order) => {
    if (!order) return false;

    const orderJs = immutableToJS(order);
    return !orderJs.remote && !orderJs.partner && orderJs.statusId === ORDER_STATUS.COMPLETED;
  };

  const goToCartPage = useCallback(() => dispatch(push(cartPath())), [dispatch]);

  const redirectToLoginWithRebookReturn = useCallback(
    (order) =>
      dispatch(
        push(loginPath, {
          nextPathname: `/account/orders/${order.id}?${DEEP_LINK_QUERY_PARAMS.REBOOK_ORDER_ID}=${order.id}`,
        }),
      ),
    [dispatch],
  );

  const rebookOrderAction = (order) => {
    if (!user) {
      redirectToLoginWithRebookReturn(order);
      return;
    }
    dispatch(rebookOrder({ order, onSuccess: goToCartPage }));
  };

  const removeRebookIdFromQueryParams = useCallback(() => {
    const currentSearch = new URLSearchParams(window.location.search);
    currentSearch.delete(DEEP_LINK_QUERY_PARAMS.REBOOK_ORDER_ID);
    history.replace({
      pathname: window.location.pathname,
      search: currentSearch.toString(),
    });
  }, [history]);

  useEffect(() => {
    const rebookOrderId = queryParams[DEEP_LINK_QUERY_PARAMS.REBOOK_ORDER_ID];
    if (!rebookOrderId) return;

    dispatch(
      rebookOrder({
        order: { id: rebookOrderId },
        onSuccess: () => {
          removeRebookIdFromQueryParams();
          goToCartPage();
        },
      }),
    );
  }, [queryParams, dispatch, goToCartPage, removeRebookIdFromQueryParams]);

  return {
    canRebookOrder,
    rebookOrder: rebookOrderAction,
  };
};

/** @typedef {import ('src/types/order').Order} Order */
/** @typedef {import ('src/types/cart').Cart} Cart */
